import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PortalShell } from "@/components/portal/PortalShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  generateSubsidyCoverageReport,
  listSubsidyCoverageReports,
} from "@/lib/subsidy-engine.functions";
import {
  generateTaxCoverageReport,
  listTaxCoverageReports,
} from "@/lib/tax-engine.functions";

export const Route = createFileRoute("/_authenticated/app/coverage")({
  component: CoveragePage,
});

function CoveragePage() {
  const qc = useQueryClient();
  const listSub = useServerFn(listSubsidyCoverageReports);
  const listTax = useServerFn(listTaxCoverageReports);
  const genSub = useServerFn(generateSubsidyCoverageReport);
  const genTax = useServerFn(generateTaxCoverageReport);

  const sub = useQuery({ queryKey: ["coverage", "subsidy"], queryFn: () => listSub() });
  const tax = useQuery({ queryKey: ["coverage", "tax"], queryFn: () => listTax() });

  const genSubM = useMutation({
    mutationFn: () => genSub(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["coverage", "subsidy"] }),
  });
  const genTaxM = useMutation({
    mutationFn: () => genTax(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["coverage", "tax"] }),
  });

  return (
    <PortalShell nav={[{ to: "/app/admin", label: "Admin" }, { to: "/app/coverage", label: "Coverage" }]}>
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-semibold">Coverage-Berichte</h1>
          <p className="text-sm text-muted-foreground">Förder- und Steuerengine – Vollständigkeitsprüfung</p>
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
          <CoverageColumn
            title="Förder-Coverage"
            reports={sub.data?.reports ?? []}
            onGenerate={() => genSubM.mutate()}
            loading={genSubM.isPending}
          />
          <CoverageColumn
            title="Steuer-Coverage"
            reports={tax.data?.reports ?? []}
            onGenerate={() => genTaxM.mutate()}
            loading={genTaxM.isPending}
          />
        </div>
      </div>
    </PortalShell>
  );
}

type CoverageRow = {
  id: string;
  generated_at: string;
  coverage_percent: number;
  summary: string | null;
  gaps: unknown;
  risks: unknown;
  recommendations: unknown;
  programs_total?: number;
  rules_total?: number;
};

function CoverageColumn({
  title, reports, onGenerate, loading,
}: { title: string; reports: CoverageRow[]; onGenerate: () => void; loading: boolean }) {
  const latest = reports[0];
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{title}</CardTitle>
        <Button size="sm" onClick={onGenerate} disabled={loading}>
          {loading ? "Erzeuge…" : "Neuen Bericht erzeugen"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {!latest ? (
          <p className="text-sm text-muted-foreground">Noch kein Bericht generiert.</p>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Badge variant={latest.coverage_percent >= 80 ? "default" : "secondary"}>
                {latest.coverage_percent}% Abdeckung
              </Badge>
              <span className="text-xs text-muted-foreground">
                {new Date(latest.generated_at).toLocaleString("de-DE")}
              </span>
            </div>
            {latest.summary && <p className="text-sm">{latest.summary}</p>}
            <Section label="Lücken" items={latest.gaps} />
            <Section label="Risiken" items={latest.risks} />
            <Section label="Empfehlungen" items={latest.recommendations} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Section({ label, items }: { label: string; items: unknown }) {
  const arr = Array.isArray(items) ? items : [];
  if (arr.length === 0) return null;
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-1">{label}</h4>
      <ul className="list-disc pl-5 space-y-1 text-sm">
        {arr.map((it, i) => (
          <li key={i}>{typeof it === "string" ? it : JSON.stringify(it)}</li>
        ))}
      </ul>
    </div>
  );
}
